import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Skeleton } from '@mui/material';

import {
  Avatar,
  Button,
  CopyToClipboard,
  Icon,
  SearchBar,
  Tooltip
} from '@components/index';
import { MixpanelEventV2, sendMixpanelEventV2 } from '@core/analytics/mixpanel';
import useIsTruncated from '@hooks/useIsTruncated';
import { Branch } from '@types';
import useOnClickOutside from '@utils/hooks';

import theme from '../../../../themes/colors';

interface BranchSwitcherProps {
  selectedBranch: string;
  branchesList: Branch[];
  handleAddNewBranch: (branchCount: number) => void;
  hasCreateBranchAccess: boolean;
  onBranchChange: (branchName: string) => void;
  isLoading: boolean;
}

interface BranchItemProps {
  branch: Branch;
  isActiveBranch: boolean;
  bgColor: string;
  onBranchChange: (branchName: string) => void;
}

const BranchItem = ({
  branch,
  isActiveBranch,
  bgColor,
  onBranchChange
}: BranchItemProps) => {
  const { ref: branchNameRef, isTruncated } = useIsTruncated<HTMLSpanElement>(
    branch?.name
  );

  return (
    <>
      <div
        className={`flex w-full cursor-pointer items-center gap-3 rounded-lg ${
          isActiveBranch ? '' : 'hover:bg-cofeeGreyBlue75'
        }`}
        onClick={() => onBranchChange(branch.name)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            onBranchChange(branch.name);
          }
        }}
        tabIndex={0}
        role="button"
      >
        <Avatar
          name={branch?.name || ''}
          className={`${
            isActiveBranch ? 'size-12 min-w-12' : 'size-10 min-w-10'
          } rounded-lg text-theme`}
          imageUrl={branch?.image_url}
          bgColor={bgColor}
        />
        <div className="flex h-full flex-1 flex-col justify-between overflow-hidden">
          <span
            ref={branchNameRef}
            className={`${
              isActiveBranch ? 'text-lg font-semibold' : 'text-base font-medium'
            } truncate text-cofeeNeutral10`}
            data-tooltip-id={`branch-name-${branch.id}`}
          >
            {branch?.name || ''}
          </span>
          <span className="flex items-center gap-1 text-sm font-medium text-cofeeNeutral8">
            ID: {branch?.id}
            <CopyToClipboard
              textToCopy={branch?.id || ''}
              iconColor="text-grey"
              onCopy={() =>
                sendMixpanelEventV2(MixpanelEventV2.BRANCH_ID_COPIED, {
                  branch_name: branch?.name || '',
                  source: 'branch_list'
                })
              }
            />
          </span>
        </div>
      </div>
      {isTruncated && (
        <Tooltip
          id={`branch-name-${branch.id}`}
          className="!max-w-80 rounded-lg !bg-cofeeNeutral10"
        >
          <div className="break-words px-3 py-2 text-sm text-cofeeNeutral1">
            {branch?.name}
          </div>
        </Tooltip>
      )}
    </>
  );
};

const MIN_BRANCH_COUNT_FOR_SEARCH = 5;

const BranchSwitcher = ({
  selectedBranch,
  branchesList,
  handleAddNewBranch,
  hasCreateBranchAccess,
  onBranchChange,
  isLoading
}: BranchSwitcherProps) => {
  const [showBranchSwitcherModal, setShowBranchSwitcherModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const modalRef = useRef() as MutableRefObject<HTMLDivElement>;

  const { currentBranchFilter } = useSelector((state: any) => state.branch);

  const currentBranchName = currentBranchFilter?.branchName;

  useOnClickOutside(modalRef, (e) => {
    e.stopPropagation();
    setShowBranchSwitcherModal(false);
  });

  useEffect(() => {
    if (!showBranchSwitcherModal) {
      setSearchTerm('');
    }
  }, [showBranchSwitcherModal]);

  const branchToFind = currentBranchName || selectedBranch;
  const currentBranch = branchesList.find(
    (branch) =>
      String(branch.name).toLowerCase() === String(branchToFind).toLowerCase()
  );

  const bgColorList = [
    theme.cofeeBlue50,
    theme.cofeeOrange50,
    theme.cofeePurple50,
    theme.cofeeGreen1
  ];

  const handleOpenBranchSwitcherModal = () => {
    sendMixpanelEventV2(MixpanelEventV2.BRANCH_SWITCH_SELECTED, {
      branch_name: currentBranchName || selectedBranch,
      branch_id: currentBranch?.id,
      branch_count: branchesList.length
    });
    if (!isLoading) {
      setShowBranchSwitcherModal((prev) => !prev);
    }
  };

  const handleCreateBranchClick = () => {
    sendMixpanelEventV2(MixpanelEventV2.CREATE_BRANCH_CLICKED, {
      branch_count: branchesList.length
    });
    handleAddNewBranch(branchesList.length);
  };

  const handleBranchChange = (branchName: string) => {
    const newBranchId = branchesList.find(
      (branch) =>
        String(branch.name).toLowerCase() === String(branchName).toLowerCase()
    )?.id;
    sendMixpanelEventV2(MixpanelEventV2.BRANCH_SWITCHED, {
      new_branch_name: branchName,
      new_branch_id: newBranchId,
      previous_branch_name: currentBranchName || selectedBranch,
      previous_branch_id: currentBranch?.id,
      branch_count: branchesList.length
    });
    onBranchChange(branchName);
  };

  const otherBranches = branchesList.filter(
    (branch) => branch.id !== currentBranch?.id
  );

  const shouldShowBranchSearch =
    otherBranches.length > MIN_BRANCH_COUNT_FOR_SEARCH;

  const searchedBranches = shouldShowBranchSearch
    ? otherBranches.filter((branch) =>
        String(branch.name)
          .toLowerCase()
          .includes(searchTerm.trim().toLowerCase())
      )
    : otherBranches;

  const renderBranchData = (branch: Branch | undefined, index: number) => {
    if (!branch) return null;

    const isActiveBranch =
      branch.name === (currentBranchName || selectedBranch);
    const bgColor = bgColorList[index % bgColorList.length];

    return (
      <BranchItem
        branch={branch}
        isActiveBranch={isActiveBranch}
        bgColor={bgColor}
        onBranchChange={handleBranchChange}
      />
    );
  };

  return (
    <div className="px-2 pb-3">
      <div
        className="relative flex h-11 w-full items-center justify-between gap-2 rounded-lg border border-cofeeNeutral4 py-2 pl-4 pr-2"
        onClick={handleOpenBranchSwitcherModal}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleOpenBranchSwitcherModal();
          }
        }}
        tabIndex={0}
        role="button"
        ref={modalRef}
      >
        {isLoading ? (
          <Skeleton variant="text" width="100%" height="100%" />
        ) : (
          <>
            <div className="flex w-full flex-col">
              <span className="text-10  text-cofeeNeutral7">Branch</span>
              <span className="max-w-36 truncate text-sm font-medium text-cofeeNeutral10">
                {currentBranchName || selectedBranch}
              </span>
            </div>
            <Icon
              name="down"
              stroke={theme.cofeeNeutral8}
              className={`size-4 ${
                showBranchSwitcherModal
                  ? 'rotate-180 transition-transform duration-300'
                  : 'rotate-0 transition-transform duration-300'
              }`}
            />
          </>
        )}
        {showBranchSwitcherModal && (
          <div className="fixed left-6 top-30 z-50 w-full max-w-90 cursor-default rounded-lg border border-cofeeNeutral4 bg-cofeeNeutral1 shadow-boxShadow1">
            <div
              className={`flex items-center gap-3 p-4  ${
                hasCreateBranchAccess || branchesList.length > 1
                  ? 'border-b border-cofeeNeutral5 '
                  : ''
              }`}
            >
              {renderBranchData(currentBranch, 3)}
            </div>
            {hasCreateBranchAccess && (
              <div className="px-4 pt-4">
                <Button
                  variant="secondary"
                  className="!mb-3 !h-12.5 w-full !justify-start !border-cofeeNeutral6 !bg-cofeeNeutral1 hover:!border-cofeePrimary2 hover:!bg-cofeeGreyBlue75"
                  label="New Branch"
                  leftIconName="plus"
                  leftIconStroke={theme.cofeePrimary6}
                  labelStyle="!text-cofeeNeutral10 !font-medium !text-base"
                  handleClick={handleCreateBranchClick}
                />
              </div>
            )}

            {shouldShowBranchSearch && (
              <div className="mt-3 px-4">
                <SearchBar
                  placeholder="Search branch"
                  value={searchTerm}
                  onSearch={setSearchTerm}
                  stopEventPropagation
                  searchBarStyle="!h-10"
                  debounceDelay={0}
                />
              </div>
            )}

            {branchesList.length > 1 && (
              <div className="mt-3 pb-4 pl-4">
                <p className="pb-2 text-sm font-medium text-cofeeNeutral8">
                  Switch Branch
                </p>
                <div
                  className={`custom-scroll mr-5px overflow-auto pr-[5px] ${
                    shouldShowBranchSearch ? 'max-h-60' : 'max-h-[300px]'
                  }`}
                >
                  {searchedBranches.length > 0 ? (
                    searchedBranches.map((branch, index) => (
                      <div
                        key={branch.id}
                        className="flex items-center gap-3  border-b border-cofeeNeutral5 py-2 last:border-b-0"
                      >
                        {renderBranchData(branch, index)}
                      </div>
                    ))
                  ) : (
                    <p className="py-2 pr-4 text-sm text-cofeeNeutral7">
                      No branches found
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {/* Todo:Remove fixed and make it absolute later */}
    </div>
  );
};

export default BranchSwitcher;
